import React from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { Box, Typography } from '@mui/material'

import { idAndNameShape } from '~/src/Lib/PropTypes'
import { EMPTY_ARRAY } from '~/src/Lib/Utils'
import AttachmentCard from '~/src/UI/Cards/Attachment'
import { parseCommentText } from '~/src/UI/Shared/DescriptionMentions'

import Base, { defaultFields } from '../Base'
import { getFirstAttachment } from '../utils'
import { formatTask } from './utils'

export const TaskFormatter = React.memo(({
  annotation,
  entities,
  inline,
  tooltip,
  ...props
}) => {
  const format = formatTask({ annotation, entities })
  const { status, category } = format

  const { attachment } = annotation
  const firstAttachment = getFirstAttachment(attachment)

  let { title, body } = format
  let { fields = EMPTY_ARRAY } = props

  if (status) {
    title = (
      <Box
        display="flex"
        alignItems="center"
        flexWrap="nowrap"
        style={{ gap: '8px' }}
      >
        <Typography noWrap color="inherit" variant="body1">
          {title}
        </Typography>
        {status}
      </Box>
    )
  }

  if (category) {
    title = (
      <Typography noWrap variant="body1" color="inherit">
        <small>{category.toUpperCase()}</small>
        <br />
        {title}
      </Typography>
    )
  }

  if (!inline && firstAttachment) {
    const { mimetype, type } = firstAttachment
    const isImage = Boolean((mimetype || type || '').match(/image/))
    body = isImage ? (
      <AttachmentCard
        {...firstAttachment}
        title={title}
        description={body}
        inline={inline}
        tooltip={tooltip}
      />
    ) : [body, firstAttachment.filename].filter(Boolean).join(' - ')
    title = isImage ? '' : title
  }

  if (!category && !fields.includes('body')) {
    fields = [...fields, 'body']
  }
  let formattedBody = body || ''
  if (typeof body === 'string') {
    formattedBody = parseCommentText(formattedBody)
  }
  return (
    <Base
      {...props}
      fields={fields}
      inline={inline}
      title={title}
      body={formattedBody}
      dateFormat="TINY_DATE"
    />
  )
})

TaskFormatter.propTypes = {
  annotation: PropTypes.shape({
    attachment: PropTypes.arrayOf(PropTypes.shape({
      filename: PropTypes.string,
      mimetype: PropTypes.string.isRequired,
      title: PropTypes.string,
      url: PropTypes.string.isRequired,
    })),
    description: PropTypes.string,
    descriptionMentions: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string.isRequired,
    completedOn: PropTypes.string,
    startedOn: PropTypes.string,
  }).isRequired,
  entities: PropTypes.objectOf(PropTypes.objectOf(idAndNameShape)).isRequired,
  fields: PropTypes.arrayOf(PropTypes.string),
  inline: PropTypes.bool,
  tooltip: PropTypes.bool,
}

TaskFormatter.defaultProps = {
  fields: defaultFields,
  inline: false,
  tooltip: true,
}

TaskFormatter.displayName = 'Annotatation/Task'

const Memoized = React.memo(props => {
  const { entities } = useConnect('selectEntities')

  return <TaskFormatter {...props} entities={entities} />
})
export default Memoized
